import { useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    AGENTIC_MIN_API_VERSION,
    FORM_DEFAULT_VALUES,
    SAGE100_API_VERSIONS,
    SAGE100_OUTPUT_MAPPER_KEYS,
    type Sage100FormData,
    type Sage100TabType,
} from './constants.ts';
import Alert from '../../../core/components/Alert.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Switch from '../../../core/components/Switch.tsx';
import TabButton from '../../../core/components/TabButton.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { OutputMapperEditorPanel } from '../../components/OutputMapper/index.ts';
import { GET_SAGE100_PRICE_LISTS } from '../../queries.ts';

const TABS: { value: Sage100TabType; label: string }[] = [
    { value: 'configuration', label: 'sage100.form.tabs.configuration' },
    { value: 'order', label: 'sage100.form.tabs.orderOutputStudio' },
    { value: 'rfq', label: 'sage100.form.tabs.rfqOutputStudio' },
];

const SectionHeader = ({ children }: { children: string }) => (
    <div className="text-lg font-semibold mt-2">{children}</div>
);

interface Sage100IntegrationFormProps {
    integration?: any;
    onSubmit: (data: Sage100FormData) => Promise<void>;
    activeTab?: Sage100TabType;
    onTabChange?: (tab: Sage100TabType) => void;
    formId: string;
}

const Sage100IntegrationForm = ({
    integration,
    onSubmit,
    activeTab = 'configuration',
    onTabChange,
    formId,
}: Sage100IntegrationFormProps) => {
    const { t } = useTranslation('integrations');
    const isEdit = Boolean(integration);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];

    // read live from Sage; the backend returns none while the stored version is below 9
    const { data: priceListData } = useQuery(GET_SAGE100_PRICE_LISTS, {
        variables: { integrationId: integration?.id },
        skip: !integration,
        fetchPolicy: 'no-cache',
    });
    const priceLists = priceListData?.sage100PriceLists || [];

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;
    const authFlow = form.watch('authFlow');
    const livePriceCheck = form.watch('livePriceCheck');
    const useOutputMapping = form.watch('useOutputMapping');
    const agenticSupported = Number(form.watch('apiVersion')) >= AGENTIC_MIN_API_VERSION;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                entitlementId: integration.entitlementId,
                dataSet: integration.dataSet,
                salesOrderType: integration.salesOrderType,
                offerDocumentType: integration.offerDocumentType || FORM_DEFAULT_VALUES.offerDocumentType,
                authState: integration.authState,
                authFlow: integration.authFlow || 'client_credentials',
                orderChannelIds: integration.orderChannels.map((c) => c.id),
                rfqChannelIds: (integration.rfqChannels ?? []).map((c) => c.id),
                apiVersion: integration.apiVersion || '',
                livePriceCheck: Boolean(integration.livePriceCheck),
                allowAgenticWrites: Boolean(integration.allowAgenticWrites),
                saveDocumentsAsDrafts: Boolean(integration.saveDocumentsAsDrafts),
                standardPriceList: integration.standardPriceList || '',
                ignoreExpiredOffers: integration.ignoreExpiredOffers ?? true,
                priceCheckBasis: integration.priceCheckBasis || FORM_DEFAULT_VALUES.priceCheckBasis,
                useOutputMapping: Boolean(integration.useOutputMapping),
            });
        }
    }, [integration]);

    return (
        <div className="max-w-6xl w-full flex flex-col flex-1 min-h-0">
            {isEdit && (
                <div className="flex border-b border-line-weak mb-4">
                    {TABS.map((tab) => (
                        <TabButton
                            key={tab.value}
                            type="button"
                            isActive={activeTab === tab.value}
                            onClick={() => onTabChange?.(tab.value)}
                        >
                            {t(tab.label)}
                        </TabButton>
                    ))}
                </div>
            )}

            {activeTab === 'configuration' && (
                <form
                    id={formId}
                    className="w-full max-w-[48rem] border border-solid border-line-weak bg-surface-weak flex flex-col rounded-lg p-8 gap-6"
                    onSubmit={form.handleSubmit(onSubmit)}
                >
                    <SectionHeader>{t('sage100.form.header.connection')}</SectionHeader>

                    <Controller
                        name="name"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('sage100.form.name')} error={formErrors.name}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="entitlementId"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('sage100.form.entitlementId')} error={formErrors.entitlementId}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="dataSet"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('sage100.form.dataSet')} error={formErrors.dataSet}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="authFlow"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('sage100.form.authFlow')} error={formErrors.authFlow}>
                                <SelectField
                                    {...field}
                                    options={[
                                        {
                                            value: 'authorization_code',
                                            label: t('sage100.form.authFlowOptions.authorizationCode'),
                                            description: t('sage100.form.authFlowOptions.authorizationCodeDescription'),
                                        },
                                        {
                                            value: 'client_credentials',
                                            label: t('sage100.form.authFlowOptions.clientCredentials'),
                                            description: t('sage100.form.authFlowOptions.clientCredentialsDescription'),
                                        },
                                    ]}
                                    placeholder={t('sage100.form.authFlowPlaceholder')}
                                />
                            </FormField>
                        )}
                    />

                    {isEdit && authFlow?.toLowerCase() === 'authorization_code' && (
                        <Controller
                            name="authState"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('sage100.form.authState')} error={formErrors.authState}>
                                    <StringField {...field} readOnly={true} />
                                </FormField>
                            )}
                        />
                    )}

                    {isEdit && (
                        <Controller
                            name="apiVersion"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sage100.form.apiVersion')}
                                    error={formErrors.apiVersion}
                                    helpText={t('sage100.form.apiVersionHelpText')}
                                >
                                    <SelectField
                                        value={field.value || null}
                                        onValueChange={(value) => {
                                            field.onChange({ target: { value } });
                                            if (Number(value) < AGENTIC_MIN_API_VERSION) {
                                                form.setValue('livePriceCheck', false);
                                                form.setValue('allowAgenticWrites', false);
                                                form.setValue('saveDocumentsAsDrafts', false);
                                                form.setValue('standardPriceList', '');
                                            }
                                        }}
                                        options={SAGE100_API_VERSIONS.map((version) => ({
                                            value: version,
                                            label: t(`sage100.form.apiVersionOptions.v${version}`),
                                        }))}
                                        placeholder={t('sage100.form.apiVersionPlaceholder')}
                                    />
                                </FormField>
                            )}
                        />
                    )}

                    <SectionHeader>{t('sage100.form.header.orders')}</SectionHeader>

                    <Controller
                        name="salesOrderType"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('sage100.form.salesOrderType')} error={formErrors.salesOrderType}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="orderChannelIds"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('sage100.form.orderChannelIds')} error={formErrors.orderChannelIds}>
                                <CheckboxGroup
                                    {...field}
                                    options={orderChannels.map((c) => ({ value: c.id, label: c.name }))}
                                    onValueChange={(value) => field.onChange({ target: { value } })}
                                />
                            </FormField>
                        )}
                    />

                    <SectionHeader>{t('sage100.form.header.offers')}</SectionHeader>

                    {isEdit && (
                        <Controller
                            name="offerDocumentType"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sage100.form.offerDocumentType')}
                                    error={formErrors.offerDocumentType}
                                    helpText={t('sage100.form.offerDocumentTypeHelpText')}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />
                    )}

                    <Controller
                        name="rfqChannelIds"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('sage100.form.rfqChannelIds')}
                                error={formErrors.rfqChannelIds}
                                helpText={t('sage100.form.rfqChannelIdsHelpText')}
                            >
                                <CheckboxGroup
                                    {...field}
                                    options={rfqChannels.map((c) => ({ value: c.id, label: c.name }))}
                                    onValueChange={(value) => field.onChange({ target: { value } })}
                                />
                            </FormField>
                        )}
                    />

                    {isEdit && (
                        <>
                            <SectionHeader>{t('sage100.form.header.agentTools')}</SectionHeader>

                            {!agenticSupported && <Alert severity="info">{t('sage100.form.requiresApiV9')}</Alert>}

                            <div
                                className={classnames('flex flex-col gap-6', !agenticSupported && 'opacity-50')}
                                aria-disabled={!agenticSupported}
                            >
                                <Controller
                                    name="livePriceCheck"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('sage100.form.livePriceCheck')}
                                            error={formErrors.livePriceCheck}
                                            helpText={t('sage100.form.livePriceCheckHelpText')}
                                        >
                                            <Switch
                                                value={field.value}
                                                onValueChange={field.onChange}
                                                disabled={!agenticSupported}
                                                aria-label={t('sage100.form.livePriceCheck')}
                                            />
                                        </FormField>
                                    )}
                                />

                                {livePriceCheck && (
                                    <Controller
                                        name="priceCheckBasis"
                                        control={form.control}
                                        render={({ field }) => (
                                            <FormField
                                                label={t('sage100.form.priceCheckBasis')}
                                                error={formErrors.priceCheckBasis}
                                            >
                                                <SelectField
                                                    {...field}
                                                    disabled={!agenticSupported}
                                                    options={[
                                                        {
                                                            value: 'unit_price',
                                                            label: t('sage100.form.priceCheckBasisOptions.unitPrice'),
                                                            description: t(
                                                                'sage100.form.priceCheckBasisOptions.unitPriceDescription'
                                                            ),
                                                        },
                                                        {
                                                            value: 'net_unit_price',
                                                            label: t(
                                                                'sage100.form.priceCheckBasisOptions.netUnitPrice'
                                                            ),
                                                            description: t(
                                                                'sage100.form.priceCheckBasisOptions.netUnitPriceDescription'
                                                            ),
                                                        },
                                                    ]}
                                                />
                                            </FormField>
                                        )}
                                    />
                                )}

                                <Controller
                                    name="allowAgenticWrites"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('sage100.form.allowAgenticWrites')}
                                            error={formErrors.allowAgenticWrites}
                                            helpText={t('sage100.form.allowAgenticWritesHelpText')}
                                        >
                                            <Switch
                                                value={field.value}
                                                onValueChange={field.onChange}
                                                disabled={!agenticSupported}
                                                aria-label={t('sage100.form.allowAgenticWrites')}
                                            />
                                        </FormField>
                                    )}
                                />

                                <Controller
                                    name="saveDocumentsAsDrafts"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('sage100.form.saveDocumentsAsDrafts')}
                                            error={formErrors.saveDocumentsAsDrafts}
                                            helpText={t('sage100.form.saveDocumentsAsDraftsHelpText')}
                                        >
                                            <Switch
                                                value={field.value}
                                                onValueChange={field.onChange}
                                                disabled={!agenticSupported}
                                                aria-label={t('sage100.form.saveDocumentsAsDrafts')}
                                            />
                                        </FormField>
                                    )}
                                />

                                <Controller
                                    name="standardPriceList"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('sage100.form.standardPriceList')}
                                            error={formErrors.standardPriceList}
                                            helpText={t('sage100.form.standardPriceListHelpText')}
                                        >
                                            <SelectField
                                                value={field.value || null}
                                                onValueChange={(value) => field.onChange(value || '')}
                                                disabled={!agenticSupported}
                                                options={[
                                                    // a stored list Sage no longer returns stays selectable, so saving keeps it
                                                    ...(field.value &&
                                                    !priceLists.some((list) => list.name === field.value)
                                                        ? [{ value: field.value, label: field.value }]
                                                        : []),
                                                    ...priceLists.map((list) => ({
                                                        value: list.name,
                                                        label: list.name,
                                                        description: list.grossPrices
                                                            ? t('sage100.form.standardPriceListGross')
                                                            : undefined,
                                                    })),
                                                ]}
                                                placeholder={t('sage100.form.standardPriceListPlaceholder')}
                                            />
                                        </FormField>
                                    )}
                                />

                                <Controller
                                    name="ignoreExpiredOffers"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('sage100.form.ignoreExpiredOffers')}
                                            error={formErrors.ignoreExpiredOffers}
                                            helpText={t('sage100.form.ignoreExpiredOffersHelpText')}
                                        >
                                            <Switch
                                                value={field.value}
                                                onValueChange={field.onChange}
                                                disabled={!agenticSupported}
                                                aria-label={t('sage100.form.ignoreExpiredOffers')}
                                            />
                                        </FormField>
                                    )}
                                />
                            </div>

                            <SectionHeader>{t('sage100.form.header.outputStudio')}</SectionHeader>

                            <Controller
                                name="useOutputMapping"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('sage100.form.useOutputMapping')}
                                        error={formErrors.useOutputMapping}
                                        helpText={t('sage100.form.useOutputMappingHelpText')}
                                    >
                                        <Switch
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            aria-label={t('sage100.form.useOutputMapping')}
                                        />
                                    </FormField>
                                )}
                            />

                            {useOutputMapping && !integration.outputMappingSaved && (
                                <Alert severity="info">{t('sage100.form.useOutputMappingDefaultHint')}</Alert>
                            )}
                        </>
                    )}
                </form>
            )}

            {activeTab !== 'configuration' && (
                <div className="w-full flex flex-col flex-1 min-h-0 border border-solid border-line-weak bg-surface-weak rounded-lg">
                    <OutputMapperEditorPanel
                        key={activeTab}
                        outputMapperKey={SAGE100_OUTPUT_MAPPER_KEYS[activeTab]}
                        ownerId={integration?.id}
                        recordType={activeTab}
                        hideConfiguration
                    />
                </div>
            )}
        </div>
    );
};

export default Sage100IntegrationForm;
